import { parseScrapedAt } from './servedTime.js'
import { evidenceTimestamp, sourceCheckMaxAgeMs } from './recommendationEvidence.js'

const CATALOGUE_SNAPSHOT_TOOLTIP =
  'Captured from AutoUncle’s catalogue. This does not mean the dealer listing was checked directly.'

const SOURCE_CHECK_COPY = Object.freeze({
  expired: Object.freeze({
    state: 'expired',
    labelKey: 'Dealer source check expired ({time}); recheck availability',
    tooltipKey: 'This earlier live check is outside the recommendation source-check window. It does not confirm availability now.',
  }),
  invalidTime: Object.freeze({
    state: 'unverified',
    labelKey: 'Dealer source check is dated in the future; availability unverified',
    tooltipKey: 'A future timestamp cannot establish a current source check. Refresh the publication; do not assume availability.',
  }),
  live: Object.freeze({
    state: 'live',
    labelKey: 'Dealer source verified live {time}',
    tooltipKey: 'The dealer or source page responded as available at this check time.',
  }),
  gone: Object.freeze({
    state: 'unavailable',
    labelKey: 'Dealer source checked {time}: listing unavailable',
    tooltipKey: 'The dealer or source page was unavailable or marked sold or reserved at this check time.',
  }),
  blocked: Object.freeze({
    state: 'unverified',
    labelKey: 'Dealer source check attempted {time}: availability unverified',
    tooltipKey: 'The dealer or source blocked the automated check. This timestamp does not confirm availability.',
  }),
  unverified: Object.freeze({
    state: 'unverified',
    labelKey: 'Dealer source status recorded {time}: availability unverified',
    tooltipKey: 'No direct dealer or source status was confirmed. This timestamp does not confirm availability.',
  }),
})

function translate(key, vars) {
  if (!vars) return key
  return key.replace(/\{(\w+)\}/g, (_, name) => vars[name] ?? `{${name}}`)
}

/**
 * Render a contract UTC timestamp without letting the browser timezone move its date.
 */
export function formatFreshnessTime(timestamp, t = translate, now = Date.now()) {
  const date = timestamp instanceof Date ? timestamp : parseScrapedAt(timestamp)
  if (!date || Number.isNaN(date.getTime())) return null

  const nowMs = now instanceof Date ? now.getTime() : Number(now)
  if (!Number.isFinite(nowMs) || date.getTime() > nowMs) return null
  const mins = Math.floor((nowMs - date.getTime()) / 60000)
  if (mins < 1) return t('just now')
  if (mins < 60) return t('{mins}m ago', { mins })

  const hours = Math.floor(mins / 60)
  if (hours < 24) return t('{hours}h ago', { hours })

  const days = Math.floor(mins / 1440)
  if (days === 1) return t('yesterday')
  if (days < 7) return t('{days}d ago', { days })

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

/**
 * A source status is displayable only when its own checked timestamp is present and valid.
 * The source flag controls whether the copy may claim a live verification.
 */
export function dealerSourceCheckPresentation(sourceCheckedAt, sourceFlag, now = Date.now()) {
  if (typeof sourceCheckedAt !== 'string' || sourceCheckedAt.trim() === '') return null

  const timestamp = evidenceTimestamp(sourceCheckedAt.trim())
  if (timestamp === null) return null
  const checkedAt = new Date(timestamp)
  const clock = now instanceof Date ? now.getTime() : Number(now)
  if (!Number.isFinite(clock)) return null
  if (timestamp > clock) return { ...SOURCE_CHECK_COPY.invalidTime, checkedAt }

  const normalizedFlag = typeof sourceFlag === 'string'
    ? sourceFlag.trim().toLowerCase()
    : ''
  const copy = normalizedFlag === 'live' && clock - timestamp > sourceCheckMaxAgeMs
    ? SOURCE_CHECK_COPY.expired : SOURCE_CHECK_COPY[normalizedFlag] || SOURCE_CHECK_COPY.unverified

  return { ...copy, checkedAt }
}

/**
 * Keep AutoUncle catalogue capture time and direct-source check time independent.
 */
export function freshnessPresentation(car, t = translate, now = Date.now()) {
  const snapshotTime = formatFreshnessTime(car?.scraped_at, t, now)
  const sourceCheck = dealerSourceCheckPresentation(car?.source_checked_at, car?.source_flag, now)
  const sourceCheckTime = sourceCheck
    ? formatFreshnessTime(sourceCheck.checkedAt, t, now)
    : null

  return {
    catalogueSnapshot: snapshotTime
      ? {
          state: 'catalogue',
          label: t('AutoUncle catalogue snapshot {time}', { time: snapshotTime }),
          tooltip: t(CATALOGUE_SNAPSHOT_TOOLTIP),
        }
      : null,
    dealerSourceCheck: sourceCheck
      ? {
          state: sourceCheck.state,
          label: t(sourceCheck.labelKey, { time: sourceCheckTime }),
          tooltip: t(sourceCheck.tooltipKey),
        }
      : null,
  }
}
